import React, { useState, useEffect } from 'react';
import {
  Card, Table, Tag, Button, Modal, Space, Form, Input, Select,
  Typography, Descriptions, message, Badge
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, EyeOutlined, BankOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const adminApi = axios.create({ baseURL: '/api', timeout: 15000 });
adminApi.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
adminApi.interceptors.response.use(r => r, error => {
  if (error.response?.status === 401) {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    window.location.href = '/admin/login';
  }
  return Promise.reject(error);
});

const statusColor = { pending: 'orange', completed: 'green', cancelled: 'red', failed: 'red', processing: 'blue' };
const statusLabel = { pending: 'Menunggu', completed: 'Selesai', cancelled: 'Dibatalkan', failed: 'Gagal', processing: 'Diproses' };

const WithdrawalApproval = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [detailVisible, setDetailVisible] = useState(false);
  const [processVisible, setProcessVisible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [processAction, setProcessAction] = useState('approve');
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchData = async (status = statusFilter) => {
    setLoading(true);
    try {
      const params = status ? { status } : {};
      const res = await adminApi.get('/admin/withdrawals', { params });
      setData(res.data?.data || []);
    } catch {
      message.error('Gagal memuat data withdrawal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleStatusChange = (val) => {
    setStatusFilter(val);
    fetchData(val);
  };

  const openProcess = (record, action) => {
    setSelected(record);
    setProcessAction(action);
    form.resetFields();
    setProcessVisible(true);
  };

  const handleProcess = async () => {
    const values = form.getFieldsValue();
    setSubmitting(true);
    try {
      await adminApi.put(`/admin/withdrawals/${selected.id}/process`, {
        action: processAction,
        notes: values.notes || null
      });
      message.success(processAction === 'approve' ? 'Withdrawal disetujui' : 'Withdrawal ditolak');
      setProcessVisible(false);
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.error || 'Gagal memproses withdrawal');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'ID Withdrawal',
      dataIndex: 'withdrawal_id',
      key: 'withdrawal_id',
      render: v => <Text code style={{ fontSize: 11 }}>{v}</Text>
    },
    {
      title: 'Merchant',
      key: 'merchant',
      render: (_, r) => (
        <div>
          <Text strong>{r.full_name || r.username || `ID: ${r.member_id}`}</Text><br />
          <Text type="secondary" style={{ fontSize: 12 }}>{r.email}</Text>
        </div>
      )
    },
    {
      title: 'Bank Tujuan',
      key: 'bank',
      render: (_, r) => (
        <div>
          <Text strong>{r.bank_name}</Text><br />
          <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.account_number}</Text><br />
          <Text type="secondary" style={{ fontSize: 12 }}>{r.account_name}</Text>
        </div>
      )
    },
    {
      title: 'Jumlah',
      key: 'amount',
      render: (_, r) => (
        <div>
          <Text>Rp {parseFloat(r.amount).toLocaleString('id-ID')}</Text><br />
          <Text type="secondary" style={{ fontSize: 12 }}>Fee: Rp {parseFloat(r.fee || 0).toLocaleString('id-ID')}</Text><br />
          <Text strong style={{ color: '#52c41a' }}>Net: Rp {parseFloat(r.net_amount).toLocaleString('id-ID')}</Text>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: s => <Tag color={statusColor[s]}>{statusLabel[s] || s}</Tag>
    },
    {
      title: 'Tgl Pengajuan',
      dataIndex: 'created_at',
      key: 'created_at',
      render: v => dayjs(v).format('DD/MM/YYYY HH:mm')
    },
    {
      title: 'Aksi',
      key: 'action',
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => { setSelected(r); setDetailVisible(true); }}>
            Detail
          </Button>
          {r.status === 'pending' && (
            <>
              <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => openProcess(r, 'approve')}>
                Setujui
              </Button>
              <Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => openProcess(r, 'reject')}>
                Tolak
              </Button>
            </>
          )}
        </Space>
      )
    }
  ];

  const pendingCount = data.filter(d => d.status === 'pending').length;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={3}><BankOutlined /> Approval Withdrawal</Title>
          <Text type="secondary">Proses permintaan pencairan saldo dari merchant</Text>
        </div>
        <Select value={statusFilter} onChange={handleStatusChange} style={{ width: 160 }}>
          <Option value="">Semua Status</Option>
          <Option value="pending">
            <Badge count={pendingCount} offset={[8, 0]}>Menunggu</Badge>
          </Option>
          <Option value="completed">Selesai</Option>
          <Option value="cancelled">Dibatalkan</Option>
        </Select>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 15, showTotal: t => `Total ${t} withdrawal` }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Detail Withdrawal"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={<Button onClick={() => setDetailVisible(false)}>Tutup</Button>}
      >
        {selected && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID"><Text code>{selected.withdrawal_id}</Text></Descriptions.Item>
            <Descriptions.Item label="Merchant">{selected.full_name || selected.username || selected.member_id}</Descriptions.Item>
            <Descriptions.Item label="Email">{selected.email || '-'}</Descriptions.Item>
            <Descriptions.Item label="Status"><Tag color={statusColor[selected.status]}>{statusLabel[selected.status]}</Tag></Descriptions.Item>
            <Descriptions.Item label="Bank">{selected.bank_name}</Descriptions.Item>
            <Descriptions.Item label="No. Rekening"><Text style={{ fontFamily: 'monospace' }}>{selected.account_number}</Text></Descriptions.Item>
            <Descriptions.Item label="Nama Pemilik">{selected.account_name}</Descriptions.Item>
            <Descriptions.Item label="Jumlah">Rp {parseFloat(selected.amount).toLocaleString('id-ID')}</Descriptions.Item>
            <Descriptions.Item label="Fee">Rp {parseFloat(selected.fee || 0).toLocaleString('id-ID')}</Descriptions.Item>
            <Descriptions.Item label="Diterima"><Text strong style={{ color: '#52c41a' }}>Rp {parseFloat(selected.net_amount).toLocaleString('id-ID')}</Text></Descriptions.Item>
            <Descriptions.Item label="Tgl Pengajuan">{dayjs(selected.created_at).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>
            {selected.processed_at && (
              <Descriptions.Item label="Tgl Diproses">{dayjs(selected.processed_at).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>
            )}
            {selected.notes && <Descriptions.Item label="Catatan">{selected.notes}</Descriptions.Item>}
          </Descriptions>
        )}
      </Modal>

      {/* Approve/Reject Modal */}
      <Modal
        title={processAction === 'approve' ? 'Setujui Withdrawal' : 'Tolak Withdrawal'}
        open={processVisible}
        onCancel={() => setProcessVisible(false)}
        footer={
          <Space>
            <Button onClick={() => setProcessVisible(false)}>Batal</Button>
            <Button
              type={processAction === 'approve' ? 'primary' : 'default'}
              danger={processAction === 'reject'}
              loading={submitting}
              onClick={handleProcess}
            >
              {processAction === 'approve' ? 'Ya, Setujui' : 'Ya, Tolak'}
            </Button>
          </Space>
        }
      >
        {selected && (
          <>
            <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Merchant">{selected.full_name || selected.username || selected.member_id}</Descriptions.Item>
              <Descriptions.Item label="Bank">{selected.bank_name} — {selected.account_number}</Descriptions.Item>
              <Descriptions.Item label="Jumlah">Rp {parseFloat(selected.amount).toLocaleString('id-ID')}</Descriptions.Item>
              <Descriptions.Item label="Net Diterima">
                <Text strong style={{ color: '#52c41a' }}>Rp {parseFloat(selected.net_amount).toLocaleString('id-ID')}</Text>
              </Descriptions.Item>
            </Descriptions>
            <Form form={form} layout="vertical">
              <Form.Item name="notes" label="Catatan (opsional)">
                <TextArea rows={3} placeholder="Catatan untuk merchant..." />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default WithdrawalApproval;
